/* eslint-disable no-useless-concat */
import { Editor } from "@tinymce/tinymce-react";
import { useRef } from "react";
import { sendFile } from "../../services/files";

type FieldType = {
  id: string;
  nome: string;
  tipo: string;
  resposta: string;
  texto_ajuda: string;
  opcoes: Array<{
    id: string;
    resposta: string;
  }>;
};

type ConditionalOptionType = {
  id: string;
  resposta: string;
  duplicar_campos: number;
  texto_ajuda: string;
  lista_campos: FieldType[];
};

type ConditionalType = {
  id: string;
  pergunta: string;
  tipo: string;
  texto_ajuda: string;
  opcoes: ConditionalOptionType[];
};

type Campo = {
  id: number;
  ordem: number;
  tipo: string;
  titulo: string;
  condicional_campos: ConditionalType[] | null;
};

type SelectorProps = Array<{
  ordem: number;
  slug: string;
  titulo: string;
  campos: Campo[];
}>;

type Props = {
  id?: string;
  value?: string;
  setValue?: (value: string) => void;
  onChange?: (value: string) => void;
  modeEdit?: "basic" | "full";
  addPlugins?: string[];
  addConf?: object;
  addToolbar?: string[];
  disabled?: boolean;
  print?: boolean;
  selectors?: SelectorProps;
};

// type SelectorFields = {
//     id: number;
//     ordem: number;
//     tipo: string;
//     titulo: string;
//     fixo?: boolean
// }

const MyEditor = (props: Props) => {
  const editor = useRef(null);
  const imageUploadHandle = async (blobInfo, success, failure) => {
    try {
      const { data } = await sendFile(blobInfo.blob());
      if (data) {
        success(data.arquivo || "");
      }
    } catch (error) {
      console.log(error);
      failure("error");
    }
  };

  const getToolbar = (isprint, mode) => {
    if (isprint) {
      return ["print"];
    }
    if (mode === "full") {
      return [
        "undo redo | bold italic underline strikethrough | fontselect fontsizeselect formatselect | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media template link anchor codesample | ltr rtl | seletores ",
      ];
    }
    return [];
  };

  const getPlugins = (modeEdit: "basic" | "full") => {
    const plugins = [];
    if (props.print) {
      plugins.push("print preview", "autoresize");
    }
    if (modeEdit === "full") {
      plugins.push(
        "print preview paste searchreplace directionality visualblocks visualchars image link table charmap hr pagebreak nonbreaking anchor toc insertdatetime advlist lists wordcount imagetools textpattern noneditable help charmap quickbars emoticons"
      );
    }
    return plugins;
  };

  const displaysConditionalSelectors = (
    campo: Campo,
    editor: { insertContent(content: string, args?: any): void }
  ) => {
    return {
      type: "nestedmenuitem",
      text: campo?.titulo,
      getSubmenuItems: () => {
        let conditional = [];
        campo.condicional_campos.map((condicional) => {
          conditional.push({
            type: "nestedmenuitem",
            text: `Opção selecionada - ${condicional.tipo}`,
            getSubmenuItems: () => {
              return condicional.opcoes.map((opcao) => ({
                type: "menuitem",
                text: opcao.resposta,
                onAction: function () {
                  editor.insertContent(
                    "<b>${" + `${opcao.id} - ${opcao.resposta}` + "}</b>"
                  );
                },
              }));
            },
          });

          return conditional.push({
            type: "nestedmenuitem",
            text: `(Condicional) - ${condicional.pergunta || campo.titulo}`,
            getSubmenuItems: () => {
              return condicional.opcoes.map((condicionalOpcao) => {
                if (
                  condicionalOpcao.lista_campos.length === 0 ||
                  condicionalOpcao.duplicar_campos > 0
                ) {
                  return {
                    type: "menuitem",
                    text: `${condicionalOpcao.resposta}`,
                    onAction: function () {
                      editor.insertContent(
                        "<b>${" +
                          `${condicionalOpcao.id} - ${condicionalOpcao.resposta}` +
                          "}</b>"
                      );
                    },
                  };
                }
                return {
                  type: "nestedmenuitem",
                  text: `(Formulário) - ${condicionalOpcao.resposta}`,
                  getSubmenuItems: () => {
                    if (
                      condicionalOpcao?.lista_campos &&
                      condicionalOpcao?.lista_campos.length > 0
                    ) {
                      return condicionalOpcao?.lista_campos.map((field) => {
                        return {
                          type: "menuitem",
                          text: `${field.nome} - ${field.tipo}`,
                          onAction: function () {
                            editor.insertContent(
                              "<b>${" + `${field.id} - ${field.nome}` + "}</b>"
                            );
                          },
                        };
                      });
                    } else {
                      return "";
                    }
                  },
                };
              });
            },
          });
        });

        return conditional;
      },
    };
  };

  const mainFixedMenu = {
    titulo: "Seletores fixos",
    campos: [
      {
        id: "numero_continuo",
        titulo: "Número continuo",
        tipo: "Texto",
        fixo: true,
      },
      {
        id: "ano_corrente",
        titulo: "Ano corrente",
        tipo: "Texto",
        fixo: true,
      },
      {
        id: "dia_mes_ano",
        titulo: "Data de hoje",
        tipo: "Data",
        fixo: true,
      },
    ],
  };

  const displayFixedSelectors = (editor: {
    insertContent(content: string, args?: any): void;
  }) => {
    return {
      type: "nestedmenuitem",
      text: mainFixedMenu.titulo,
      getSubmenuItems: () => {
        let fixed = [];
        mainFixedMenu.campos.forEach((campo) => {
          fixed.push({
            type: "menuitem",
            text: campo.titulo,
            onAction: function () {
              editor.insertContent("<b>${" + `${campo.id}` + "}</b>");
            },
          });
        });

        return fixed;
      },
    };
  };

  return (
    <Editor
      tinymceScriptSrc="/tinymce/js/tinymce/tinymce.min.js"
      ref={editor}
      value={props.value}
      id={props.id}
      onEditorChange={(content) => {
        props.setValue && props.setValue(content);
      }}
      init={{
        license_key: "gpl",
        language: "pt_BR",
        language_url: "/langs/pt_BR.js",
        fontsize_formats:
          "8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt",
        content_style: "@page { size: A4 portrait; margin: 10mm 20mm;}",
        plugins: [...getPlugins(props.modeEdit), ...(props?.addPlugins || [])],
        toolbar_sticky: true,
        autoresize: true,
        images_upload_handler: imageUploadHandle,
        toolbar: [
          ...getToolbar(props.print, props.modeEdit),
          ...(props?.addToolbar || []),
        ],
        menubar: props.print ? "" : "file edit view insert format tools table",
        image_caption: true,
        promotion: false,
        quickbars_selection_toolbar:
          "fontselect fontsizeselect formatselect |alignleft aligncenter alignright alignjustify bold italic | quicklink h2 h3 blockquote quickimage quicktable seletores",
        contextmenu: "link image imagetools table",
        quickbars_insert_toolbar: "seletores",
        ...props.addConf,
        ...props.addToolbar,
        setup: function (editor) {
          editor.ui.registry.addMenuButton("marginsPrint", {
            text: "Margens de impressão",
            fetch: function (callback) {
              let items: any[] = [];
              callback(items);
            },
          });
          if (props.selectors) {
            editor.ui.registry.addMenuButton("seletores", {
              text: "{} Seletores",
              fetch: function (callback) {
                let items: any[] = [];
                items.push(displayFixedSelectors(editor));
                props.selectors.forEach((select) => {
                  items.push({
                    type: "nestedmenuitem",
                    text: select?.titulo,
                    getSubmenuItems: () => {
                      if (select?.campos?.length > 0) {
                        return select.campos.map((campo) => {
                          if (campo.tipo === "Condicional") {
                            return displaysConditionalSelectors(campo, editor);
                          } else {
                            return {
                              type: "menuitem",
                              text: campo?.titulo,
                              onAction: function () {
                                editor.insertContent(
                                  "<b>${" +
                                    `${campo.id} - ${campo.titulo}` +
                                    "}</b>"
                                );
                              },
                            };
                          }
                        });
                      } else {
                        return "";
                      }
                    },
                  });
                });

                callback(items);
              },
            });
          }
          if (!props.print) return;
          editor.on("click", (e: any) => {
            const { target } = e;
            if (target.tagName === "A" && target.href) {
              window.open(target.href, "_blank");
            }
          });
        },
      }}
      disabled={props.disabled}
    />
  );
};

export default MyEditor;
